import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, resolveComponent as _resolveComponent, withCtx as _withCtx, resolveDirective as _resolveDirective, withDirectives as _withDirectives, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-5b7fbb1f"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "operates" }
const _hoisted_2 = {
  key: 1,
  class: "drag-mask"
}

import { nextTick, onMounted, onUnmounted, provide, ref, watch, watchEffect } from 'vue'
import { throttle } from 'lodash'
import { storeToRefs } from 'pinia'
import { useMainStore, useSlidesStore, useKeyboardStore } from '@/store'
import { ContextmenuItem } from '@/components/Contextmenu/types'
import { PPTElement } from '@/types/slides'
import { AlignmentLineProps } from '@/types/edit'
import { injectKeySlideScale } from '@/types/injectKey'
import { removeAllRanges } from '@/utils/selection'
import { KEYS } from '@/configs/hotkey'

import useViewportSize from './hooks/useViewportSize'
import useMouseSelection from './hooks/useMouseSelection'
import useDropImageOrText from './hooks/useDropImageOrText'
import useRotateElement from './hooks/useRotateElement'
import useScaleElement from './hooks/useScaleElement'
import useSelectElement from './hooks/useSelectElement'
import useDragElement from './hooks/useDragElement'
import useDragLineElement from './hooks/useDragLineElement'
import useMoveShapeKeypoint from './hooks/useMoveShapeKeypoint'
import useInsertFromCreateSelection from './hooks/useInsertFromCreateSelection'

import useDeleteElement from '@/hooks/useDeleteElement'
import useCopyAndPasteElement from '@/hooks/useCopyAndPasteElement'
import useSelectAllElement from '@/hooks/useSelectAllElement'
import useScaleCanvas from '@/hooks/useScaleCanvas'
import useScreening from '@/hooks/useScreening'
import useSlideHandler from '@/hooks/useSlideHandler'

import EditableElement from './EditableElement.vue'
import MouseSelection from './MouseSelection.vue'
import ViewportBackground from './ViewportBackground.vue'
import AlignmentLine from './AlignmentLine.vue'
import Ruler from './Ruler.vue'
import ElementCreateSelection from './ElementCreateSelection.vue'
import MultiSelectOperate from './Operate/MultiSelectOperate.vue'
import Operate from './Operate/index.vue'
import LinkDialog from './LinkDialog.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const mainStore = useMainStore()
const {
  activeElementIdList,
  activeGroupElementId,
  handleElementId,
  editorAreaFocus,
  gridLineSize,
  showRuler,
  creatingElement,
  canvasScale,
  textFormatPainter,
} = storeToRefs(mainStore)
const { currentSlide } = storeToRefs(useSlidesStore())
const { ctrlKeyState, spaceKeyState } = storeToRefs(useKeyboardStore())

const viewportRef = ref<HTMLElement>()
const alignmentLines = ref<AlignmentLineProps[]>([])

const linkDialogVisible = ref(false)
const openLinkDialog = () => linkDialogVisible.value = true

watch(handleElementId, () => {
  mainStore.setActiveGroupElementId('')
})

const elementList = ref<PPTElement[]>([])
const setLocalElementList = () => {
  elementList.value = currentSlide.value ? JSON.parse(JSON.stringify(currentSlide.value.elements)) : []
}
watchEffect(setLocalElementList)

const canvasRef = ref<HTMLElement>()
const { dragViewport, viewportStyles } = useViewportSize(canvasRef)

useDropImageOrText(canvasRef)

const { mouseSelection, mouseSelectionVisible, mouseSelectionQuadrant, updateMouseSelection } = useMouseSelection(elementList, viewportRef)

const { dragElement } = useDragElement(elementList, alignmentLines, canvasScale)
const { dragLineElement } = useDragLineElement(elementList)
const { selectElement } = useSelectElement(elementList, dragElement)
const { scaleElement, scaleMultiElement } = useScaleElement(elementList, alignmentLines, canvasScale)
const { rotateElement } = useRotateElement(elementList, viewportRef)
const { moveShapeKeypoint } = useMoveShapeKeypoint(elementList, canvasScale)

const { selectAllElement } = useSelectAllElement()
const { deleteAllElements } = useDeleteElement()
const { pasteElement } = useCopyAndPasteElement()
const { enterScreeningFromStart } = useScreening()
const { updateSlideIndex } = useSlideHandler()

// 组件渲染时，如果存在元素焦点，需要清除
// 这种情况存在于：有焦点元素的情况下进入了放映模式，再退出时，需要清除原先的焦点（因为可能已经切换了页面）
onMounted(() => {
  if (activeElementIdList.value.length) {
    nextTick(() => mainStore.setActiveElementIdList([]))
  }
})

// 点击画布的空白区域：清空焦点元素、设置画布焦点、清除文字选区、清空格式刷状态
const handleClickBlankArea = (e: MouseEvent) => {
  if (activeElementIdList.value.length) mainStore.setActiveElementIdList([])

  if (!spaceKeyState.value) updateMouseSelection(e)
  else dragViewport(e)

  if (!editorAreaFocus.value) mainStore.setEditorareaFocus(true)
  if (textFormatPainter.value) mainStore.setTextFormatPainter(null)
  removeAllRanges()
}

// 画布注销时清空格式刷状态
onUnmounted(() => {
  if (textFormatPainter.value) mainStore.setTextFormatPainter(null)
})

// 移除画布编辑区域焦点
const removeEditorAreaFocus = () => {
  if (editorAreaFocus.value) mainStore.setEditorareaFocus(false)
}

// 滚动鼠标
const { scaleCanvas } = useScaleCanvas()
const throttleScaleCanvas = throttle(scaleCanvas, 100, { leading: true, trailing: false })
const throttleUpdateSlideIndex = throttle(updateSlideIndex, 300, { leading: true, trailing: false })

const handleMousewheelCanvas = (e: WheelEvent) => {
  e.preventDefault()

  // 按住Ctrl键时：缩放画布
  if (ctrlKeyState.value) {
    if (e.deltaY > 0) throttleScaleCanvas('-')
    else if (e.deltaY < 0) throttleScaleCanvas('+')
  }
  // 上下翻页
  else {
    if (e.deltaY > 0) throttleUpdateSlideIndex(KEYS.DOWN)
    else if (e.deltaY < 0) throttleUpdateSlideIndex(KEYS.UP)
  }
}

// 开关标尺
const toggleRuler = () => {
  mainStore.setRulerState(!showRuler.value)
}

// 在鼠标绘制的范围插入元素
const { insertElementFromCreateSelection } = useInsertFromCreateSelection(viewportRef)

const contextmenus = (): ContextmenuItem[] => {
  return [
    {
      text: '粘贴',
      subText: 'Ctrl + V',
      handler: pasteElement,
    },
    {
      text: '全选',
      subText: 'Ctrl + A',
      handler: selectAllElement,
    },
    {
      text: '标尺',
      subText: showRuler.value ? '√' : '',
      handler: toggleRuler,
    },
    {
      text: '网格线',
      handler: () => mainStore.setGridLineSize(gridLineSize.value ? 0 : 50),
      children: [
        {
          text: '无',
          subText: gridLineSize.value === 0 ? '√' : '',
          handler: () => mainStore.setGridLineSize(0),
        },
        {
          text: '小',
          subText: gridLineSize.value === 25 ? '√' : '',
          handler: () => mainStore.setGridLineSize(25),
        },
        {
          text: '中',
          subText: gridLineSize.value === 50 ? '√' : '',
          handler: () => mainStore.setGridLineSize(50),
        },
        {
          text: '大',
          subText: gridLineSize.value === 100 ? '√' : '',
          handler: () => mainStore.setGridLineSize(100),
        },
      ],
    },
    {
      text: '重置当前页',
      handler: deleteAllElements,
    },
    { divider: true },
    {
      text: '幻灯片放映',
      subText: 'F5',
      handler: enterScreeningFromStart,
    },
  ]
}

provide(injectKeySlideScale, canvasScale)

return (_ctx: any,_cache: any) => {
  const _component_Modal = _resolveComponent("Modal")!
  const _directive_contextmenu = _resolveDirective("contextmenu")!
  const _directive_click_outside = _resolveDirective("click-outside")!

  return _withDirectives((_openBlock(), _createElementBlock("div", {
    class: "canvas",
    ref_key: "canvasRef",
    ref: canvasRef,
    onWheel: _cache[3] || (_cache[3] = $event => handleMousewheelCanvas($event)),
    onMousedown: _cache[4] || (_cache[4] = $event => handleClickBlankArea($event))
  }, [
    (_unref(creatingElement))
      ? (_openBlock(), _createBlock(ElementCreateSelection, {
          key: 0,
          onCreated: _cache[0] || (_cache[0] = data => _unref(insertElementFromCreateSelection)(data))
        }))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: "viewport-wrapper",
      style: _normalizeStyle({
        width: _unref(viewportStyles).width * _unref(canvasScale) + 'px',
        height: _unref(viewportStyles).height * _unref(canvasScale) + 'px',
        left: _unref(viewportStyles).left + 'px',
        top: _unref(viewportStyles).top + 'px',
      })
    }, [
      _createElementVNode("div", _hoisted_1, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(alignmentLines.value, (line, index) => {
          return (_openBlock(), _createBlock(AlignmentLine, {
            key: index,
            type: line.type,
            axis: line.axis,
            length: line.length,
            canvasScale: _unref(canvasScale)
          }, null, 8, ["type", "axis", "length", "canvasScale"]))
        }), 128)),
        (_unref(activeElementIdList).length > 1)
          ? (_openBlock(), _createBlock(MultiSelectOperate, {
              key: 0,
              elementList: elementList.value,
              scaleMultiElement: _unref(scaleMultiElement)
            }, null, 8, ["elementList", "scaleMultiElement"]))
          : _createCommentVNode("", true),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(elementList.value, (element) => {
          return (_openBlock(), _createBlock(Operate, {
            key: element.id,
            elementInfo: element,
            isSelected: _unref(activeElementIdList).includes(element.id),
            isActive: _unref(handleElementId) === element.id,
            isActiveGroupElement: _unref(activeGroupElementId) === element.id,
            isMultiSelect: _unref(activeElementIdList).length > 1,
            rotateElement: _unref(rotateElement),
            scaleElement: _unref(scaleElement),
            openLinkDialog: openLinkDialog,
            dragLineElement: _unref(dragLineElement),
            moveShapeKeypoint: _unref(moveShapeKeypoint)
          }, null, 8, ["elementInfo", "isSelected", "isActive", "isActiveGroupElement", "isMultiSelect", "rotateElement", "scaleElement", "dragLineElement", "moveShapeKeypoint"]))
        }), 128)),
        _createVNode(ViewportBackground)
      ]),
      _createElementVNode("div", {
        class: "viewport",
        ref_key: "viewportRef",
        ref: viewportRef,
        style: _normalizeStyle({ transform: `scale(${_unref(canvasScale)})` })
      }, [
        (_unref(mouseSelectionVisible))
          ? (_openBlock(), _createBlock(MouseSelection, {
              key: 0,
              top: _unref(mouseSelection).top,
              left: _unref(mouseSelection).left,
              width: _unref(mouseSelection).width,
              height: _unref(mouseSelection).height,
              quadrant: _unref(mouseSelectionQuadrant)
            }, null, 8, ["top", "left", "width", "height", "quadrant"]))
          : _createCommentVNode("", true),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(elementList.value, (element, index) => {
          return (_openBlock(), _createBlock(EditableElement, {
            key: element.id,
            elementInfo: element,
            elementIndex: index + 1,
            isMultiSelect: _unref(activeElementIdList).length > 1,
            selectElement: _unref(selectElement),
            openLinkDialog: openLinkDialog
          }, null, 8, ["elementInfo", "elementIndex", "isMultiSelect", "selectElement"]))
        }), 128))
      ], 4)
    ], 4),
    (_unref(spaceKeyState))
      ? (_openBlock(), _createElementBlock("div", _hoisted_2))
      : _createCommentVNode("", true),
    (_unref(showRuler))
      ? (_openBlock(), _createBlock(Ruler, {
          key: 2,
          viewportStyles: _unref(viewportStyles)
        }, null, 8, ["viewportStyles"]))
      : _createCommentVNode("", true),
    _createVNode(_component_Modal, {
      visible: linkDialogVisible.value,
      "onUpdate:visible": _cache[2] || (_cache[2] = ($event: any) => ((linkDialogVisible).value = $event)),
      footer: null,
      centered: "",
      width: 540,
      destroyOnClose: ""
    }, {
      default: _withCtx(() => [
        _createVNode(LinkDialog, {
          onClose: _cache[1] || (_cache[1] = ($event: any) => (linkDialogVisible.value = false))
        })
      ]),
      _: 1
    }, 8, ["visible"])
  ], 32)), [
    [_directive_contextmenu, contextmenus],
    [_directive_click_outside, removeEditorAreaFocus]
  ])
}
}

})